import { cloneElement, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconApplicants } from '@components/icons/applicants';
import { IconCompany } from '@components/icons/company';
import { IconFile } from '@components/icons/file';
import { IconHelp } from '@components/icons/help';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconReplace } from '@components/icons/replace';
import { IconSend } from '@components/icons/send';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { IconTriggerOff } from '@components/icons/trigger-off';
import { Spinner } from '@components/loading-spinner';
import { Modal, hideModal, useModal } from '@components/modal';
import { NumberField } from '@components/number-field';
import { Select } from '@components/select';
import { SelectOption } from '@components/selectmulti';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { TextAreaDynamic } from '@components/textarea/textarea-dynamic';
import { ToggleGroup } from '@components/toggle-group';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { match } from '@utils/match';

import { useGenerateCode, useTestCode } from 'api/code/hooks';
import { VariableValue } from 'api/types';
import { useNodes } from 'ui/journey-builder/hooks/use-nodes';
import { JourneyNode } from 'ui/journey-builder/node-types/types';

import { CodeData } from './common';

import { ActionIcon } from '../action-icon';
import { actionLabels } from '../action-label';

const Form = createForm<CodeData>();

export const useCodeEditorModal = () => useModal(CodeEditorModal, { variant: 'FULLSCREEN' });

const DEFAULT_PROCESS = `def process(**kwargs):
  """
  Here you can specify the output variables of your function in the 'Returns' section. The format should be:
  variable_name (variable_type): Variable label
  where variable_type is one of the following: text, email, phone, date, number, paragraph, array_of_strings, array, boolean, decimal
  Returns:
  message (text): Response message
  """
  return {
    "message": "Hello, world!"
  }`;

const RANDOM_PROCESS = `def process(**kwargs):
  """Returns:
  random_boolean (boolean): Random true/false.
  random_number (number): Random 1-10.
  random_string (text): Random string length 6.
  """
  random_string = ''.join(random.choices('abcdefghijklmnopqrstuvwxyz', k=6))
  return {
    'random_boolean': random.choice([True, False]),
    'random_number': random.randint(1, 10),
    'random_string': random_string
  }
`;

const FIRST_DAY_NEXT_MONTH_PROCESS = `def process(**kwargs):
  """Returns:
  first_day_of_next_month (date): First day of next month.
  """
  today = datetime.datetime.now()
  first_day_next_month = today.replace(day=1)
  if today.month == 12:
    first_day_next_month = first_day_next_month.replace(year=today.year + 1, month=1)
  else:
    first_day_next_month = first_day_next_month.replace(month=today.month + 1)

  return {
    "first_day_of_next_month": first_day_next_month.date()
  }`;

const NEXT_WEEKDAY_PROCESS = `def process(**kwargs):
  """
  Returns:
  next_weekday (date): Next non-weekend day.
  """

  weekdays = {0, 1, 2, 3, 4}  # Monday to Friday

  today = datetime.datetime.now()
  next_day = today

  while next_day.weekday() not in weekdays:
    next_day += datetime.timedelta(days=1)

  return {
    "next_weekday": next_day.date()
  }`;

const CodeEditorModal = ({
  inputs = [],
  code = DEFAULT_PROCESS,
  setValue,
}: {
  inputs: VariableValue[];
  code: string;
  setValue: (id: string, newValue: unknown) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const { mutateAsync: generateCode, isLoading } = useGenerateCode();
  const [generatedCode, setGeneratedCode] = useState(code);
  const codeValue = formStore.useFieldValue('code') as string;
  const [prompt, setPrompt] = useState('');
  const [hasSyntaxError, setHasSyntaxError] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const { getValidNodesMap } = useNodes();
  const validNodesMap = getValidNodesMap();
  const inputNames: string[] = inputs
    .map((inputObject) => {
      return getVariableName(inputObject);
    })
    .filter((name) => name !== '');

  const exampleChoices: SelectOption[] = [
    { label: t('randomObjects'), value: RANDOM_PROCESS },
    { label: t('firstDayNextMonth'), value: FIRST_DAY_NEXT_MONTH_PROCESS },
    { label: t('nextWeekday'), value: NEXT_WEEKDAY_PROCESS },
  ];

  return (
    <Modal>
      {isLoading && (
        <>
          <FullScreenLoader />
          <Spinner />
        </>
      )}
      <Form
        onSubmit={(values) => {
          setValue('code', values.code);
          hideModal();
        }}
        store={formStore}
      >
        <Modal.FullscreenHeader
          title={t('title')}
          actions={
            <Button
              iconLeft={<IconHelp />}
              variant="ghost"
              text={t('helpArticle')}
              href="https://help.journeys.50skills.app/en/articles/254664-code-action"
              target="_blank"
            />
          }
        />
        <Modal.Content>
          <Stack space={4}>
            <HideContainer
              title={t('variables')}
              counter={inputs.length}
              defaultClosed={inputs.length === 0}
            >
              <Stack space={2}>
                {inputs.length === 0 ? (
                  <div>
                    <Card variant="gray" space={4}>
                      <Stack orientation="horizontal" align="center" space={4}>
                        <IconTriggerOff color="textSecondary" />
                        <Text
                          text={t('noVariablesConfigured')}
                          color="textSecondary"
                          font="semibold14"
                        />
                      </Stack>
                    </Card>
                  </div>
                ) : (
                  <>
                    <Text text={t('availableVariables')} />
                    <Stack space={2} style={{ maxHeight: '320px' }}>
                      {inputs.map((variable) => (
                        <VariableCard
                          key={variable.value}
                          variable={variable}
                          nodes={validNodesMap}
                        />
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>
            </HideContainer>
            <Stack space={4}>
              <Separator />
              <Text text="AI Prompt" font="semibold14" aiGradient />
              <TextAreaDynamic
                onChange={setPrompt}
                placeholder={t('aiPlaceholder')}
                helpText="The AI can make mistakes, so always double-check the generated code"
              />
              <Stack orientation="horizontal" justify="space-between">
                <Stack space={2} orientation="horizontal">
                  <Button
                    onClick={async () => {
                      const response = await generateCode({
                        prompt,
                        availableVariables: inputNames,
                        currentCode: isUpdate ? codeValue : undefined,
                      });
                      setGeneratedCode(response.code);
                    }}
                    iconLeft={<IconReplace />}
                    variant="secondary"
                    text={t('generate')}
                  />
                  <ToggleGroup.Root
                    defaultValue={isUpdate ? 'update' : 'replace'}
                    onChange={(value) => {
                      match(value, {
                        update: () => setIsUpdate(true),
                        replace: () => setIsUpdate(false),
                      });
                    }}
                  >
                    <ToggleGroup.Button
                      text={t('update')}
                      value="update"
                      variant="orange"
                      tooltip={t('updateTooltip')}
                    />
                    <ToggleGroup.Button
                      text={t('replace')}
                      value="replace"
                      variant="blue"
                      tooltip={t('replaceTooltip')}
                    />
                  </ToggleGroup.Root>
                </Stack>
                <div style={{ width: pxToRem(419) }}>
                  <Select
                    placeholder={t('example')}
                    options={exampleChoices}
                    onChange={(value) => setGeneratedCode(value)}
                  />
                </div>
              </Stack>
            </Stack>
            <Form.PythonEditor
              key={generatedCode}
              id="code"
              label={t('code')}
              defaultValue={generatedCode}
              onCodeErrorChange={setHasSyntaxError}
            />
            <Separator />
            <TestSection inputs={inputs} code={codeValue} />
            <Separator />
          </Stack>
        </Modal.Content>
        <Modal.FullscreenFooter>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              text={t('core.save')}
              variant="primary-blue"
              isDisabled={hasSyntaxError}
              tooltip={hasSyntaxError ? t('hasSyntaxError') : undefined}
            />
          </Form.Actions>
        </Modal.FullscreenFooter>
      </Form>
    </Modal>
  );
};

const TestSection = ({ inputs, code }: { inputs: VariableValue[]; code: string }) => {
  const [testResults, setTestResults] = useState({});
  const [inputValues, setInputValues] = useState<{ [key: string]: any }>({});
  const { mutateAsync: testCode, isLoading } = useTestCode();
  const handleInputChange = (key: string, value: any) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
  };
  return (
    <HideContainer title={t('test')} defaultClosed>
      <Stack space={4}>
        {isLoading && <Spinner />}
        {inputs.map((variable) => {
          if (!variable.variables) {
            return null;
          }
          const variableObject = Object.values(variable.variables)[0];
          const key = getVariableName(variable);
          const type = variableObject.type;
          if (type === 'decimal' || type === 'number') {
            return (
              <NumberField
                key={key}
                label={variableObject.label}
                defaultValue={inputValues[key] as number | undefined}
                onChange={(value) => handleInputChange(key, value)}
                isDecimal={type === 'decimal'}
              />
            );
          }
          return (
            <TextField
              key={key}
              label={variableObject.label}
              value={inputValues[key] || ''}
              onChange={(value) => handleInputChange(key, value)}
            />
          );
        })}
        {testResults && <CodeBlock header={t('results')} data={testResults} />}
        <Stack orientation="horizontal">
          <Button
            onClick={async () => {
              const response = await testCode({ code, inputs: inputValues });
              setTestResults(response);
            }}
            iconLeft={<IconSend />}
            variant="secondary"
            text={t('test')}
          />
        </Stack>
      </Stack>
    </HideContainer>
  );
};

const getVariableName = (inputObject: VariableValue) => {
  if (inputObject.variables) {
    const variable = Object.values(inputObject.variables)[0];
    if (variable.source === 'action') {
      return `${variable.action}__${variable.field}`;
    } else {
      return `${variable.source}__${variable.field}`;
    }
  }
  return '';
};

type VariableCardProps = {
  variable: VariableValue;
  nodes: Record<string, JourneyNode>;
};

const VariableCard = ({ variable, nodes }: VariableCardProps) => {
  if (!variable.variables) {
    return null;
  }
  const variableObject = Object.values(variable.variables)[0];
  let parent, Icon, description;
  if (variableObject.source === 'action') {
    const actionNode = nodes[variableObject.action];
    if (!actionNode) {
      return (
        <Card space={4}>
          <Stack space={1}>
            <Text text={t('actionNotFound')} font="semibold14" color="red100" />
          </Stack>
        </Card>
      );
    } else {
      parent = actionLabels[actionNode.type];
      Icon = <ActionIcon action={actionNode.type} />;
      description = actionNode?.data.description ? ` (${actionNode?.data?.description})` : '';
    }
  } else {
    const source = variableObject.source.split('__')[0] as keyof typeof sourceMap;
    const sourceItem = sourceMap[source];
    description = '';
    parent = sourceItem?.label;
    Icon = sourceItem?.icon;
  }
  Icon = cloneElement(Icon, { size: 16 });

  return (
    <Card space={4}>
      <Stack space={1}>
        <Text text={getVariableName(variable)} font="semibold14" />
        <IconText icon={Icon} text={`${parent}${description}`} />
        <IconText
          icon={
            <>
              <IconSubIndicator size={16} color="buttonIconPositive" />
              <IconFile size={16} color="buttonIconPositive" />
            </>
          }
          text={variableObject.label}
        />
      </Stack>
    </Card>
  );
};

const IconText = ({ icon: Icon, text }: { icon: JSX.Element; text: string }) => {
  return (
    <Stack space={1} orientation="horizontal" align="center">
      {Icon}
      <Text font="regular12" text={text} />
    </Stack>
  );
};

const locale = {
  en: {
    title: 'Code editor',
    code: 'Code',
    helpArticle: 'Help article',
    variables: 'Variables',
    noVariablesConfigured: 'No variables configured',
    availableVariables: 'The following variables are available in kwargs dictionary:',
    aiPlaceholder: 'Describe your function and have AI generate it for you...',
    traveller: 'Traveller',
    company: 'Company',
    journey: 'Journey',
    journeyAttribute: 'Journey field',
    companyTravellerAttribute: 'Company traveller field',
    generate: 'Generate',
    update: 'Update',
    updateTooltip: 'Use existing code in prompt',
    replace: 'Replace',
    replaceTooltip: 'Replace existing code with prompt',
    test: 'Test',
    example: 'Example',
    randomObjects: 'Random objects',
    firstDayNextMonth: 'First day of next month',
    nextWeekday: 'Next weekday',
    results: 'Results',
    hasSyntaxError: 'Code contains a syntax error',
    actionNotFound: 'Action not found',
  },
};

const { t } = getScopedTranslate(locale);

const sourceMap = {
  company_traveller: {
    icon: <IconApplicants />,
    label: t('traveller'),
  },
  traveller: {
    icon: <IconApplicants />,
    label: t('traveller'),
  },
  company: {
    icon: <IconCompany />,
    label: t('company'),
  },
  journey: {
    icon: <IconJourneyItem />,
    label: t('journey'),
  },
  traveller_journey: {
    icon: <IconJourneyItem />,
    label: t('journey'),
  },
  journey_attribute: {
    icon: <IconJourneyItem />,
    label: t('journeyAttribute'),
  },
  company_traveller_attribute: {
    icon: <IconJourneyItem />,
    label: t('companyTravellerAttribute'),
  },
};

const FullScreenLoader = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  zIndex: 999999,
});
